import type { ConversationLanguageCode } from 'lib-shared-languages';
import type { Conversation } from 'lib-shared-legacy-types';
import { Channel } from 'lib-shared-types-channels';
import { PropsWithChildren } from 'lib-shared-types-utils';
import {
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { summaryVisibilityStateDefault } from 'apis/userStorage';
import { getChannelMedia } from 'utils/channels';
import { useCopilotTrial } from 'utils/useCopilotTrial';
import { useServerStorage } from 'utils/useServerStorage';

import {
  useConversationPreferredLanguage,
  useConversationSummaryVisibility,
} from './utils';

export type ConversationContextValue = {
  /** The conversation data. */
  readonly conversation?: Conversation;
  /**
   * The code of the language to which the textual messages should be translated
   * to.
   */
  readonly translationLanguageCode?: ConversationLanguageCode;
  /** The function for settings the translation language code. */
  readonly setTranslationLanguageCode?: Dispatch<
    SetStateAction<ConversationLanguageCode | undefined>
  >;
  readonly isAutoTranslating?: boolean;
  readonly isAutoSummarizing?: boolean;
} & ReturnType<typeof useConversationPreferredLanguage> &
  ReturnType<typeof useConversationSummaryVisibility>;

export const ConversationContext = createContext<ConversationContextValue>({});

type ConversationContextProviderProps = PropsWithChildren & {
  /** The conversation data. */
  conversation: Conversation;
};

export const ConversationContextProvider = ({
  conversation,
  children,
}: ConversationContextProviderProps): JSX.Element => {
  const csid = conversation.id ?? -1;
  const channel = conversation.initial_channel;
  const [translationLanguageCode, setTranslationLanguageCode] =
    useState<ConversationLanguageCode>();
  const { data: userStorage } = useServerStorage('user');
  const { hasCopilot } = useCopilotTrial();

  let media = '';
  try {
    media = channel ? getChannelMedia(channel as Channel) : '';
  } catch (error) {}
  const isAutoTranslating =
    hasCopilot &&
    userStorage['agent_copilot']?.translationVisibilityState?.[media] ===
      'auto';
  const summaryVisibilityState = {
    ...summaryVisibilityStateDefault,
    ...userStorage['agent_copilot']?.summaryVisibilityState,
  };
  const isAutoSummarizing =
    hasCopilot && summaryVisibilityState[media] === 'auto';

  const visibilityValues = useConversationSummaryVisibility(csid, channel);
  const languageValues = useConversationPreferredLanguage();

  const value = useMemo<ConversationContextValue>(
    () => ({
      ...visibilityValues,
      ...languageValues,
      translationLanguageCode,
      setTranslationLanguageCode,
      conversation,
      isAutoTranslating,
      isAutoSummarizing,
    }),
    [
      visibilityValues,
      languageValues,
      translationLanguageCode,
      conversation,
      isAutoTranslating,
      isAutoSummarizing,
    ],
  );

  // Resets the translation language code when conversation id changes.
  useEffect(() => {
    if (hasCopilot && csid > 0 && conversation.initial_channel) {
      try {
        const media = getChannelMedia(conversation.initial_channel as Channel);
        const conversationLanguage = conversation?.attributes?.language;
        const shouldAutoTranslate =
          userStorage.agent_copilot?.translationVisibilityState?.[media] ===
          'auto';
        const { preferredLanguageCode } = languageValues;
        if (
          shouldAutoTranslate &&
          preferredLanguageCode &&
          translationLanguageCode === preferredLanguageCode
        ) {
          // translation language is already set to the preferred language
          return;
        }
        if (
          preferredLanguageCode &&
          conversationLanguage === preferredLanguageCode
        ) {
          // conversation language is the same as the preferred language
          return setTranslationLanguageCode(undefined);
        }
        if (shouldAutoTranslate && preferredLanguageCode) {
          // auto translate is enabled for the media
          return setTranslationLanguageCode(preferredLanguageCode);
        }
      } catch (error) {
        // getChannelMedia throws if the channel is not recognized
        // the default fallback of "no translation language" (undefined) is sufficient
      }
    }

    return setTranslationLanguageCode(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csid]);

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
};
